import { useState } from "react";
import { useLazyGetReportDownloadUrlQuery } from "@/services";
import useSnackbar from "./useSnackbar";

interface UseReportDownloadReturn {
  downloadReport: (reportId: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  downloadingReports: string[];
}

export const useReportDownload = (): UseReportDownloadReturn => {
  const [getDownloadUrl] = useLazyGetReportDownloadUrlQuery();
  const [downloadingReports, setDownloadingReports] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { show } = useSnackbar();

  const downloadReport = async (reportId: string) => {
    if (downloadingReports.includes(reportId)) {
      return; // Already downloading
    }

    setDownloadingReports(prev => [...prev, reportId]);
    setError(null);

    try {
      const result = await getDownloadUrl({ reportId });
      
      if (result.error) {
        let errorMessage = 'Failed to generate download URL';
        if ('data' in result.error && typeof result.error.data === 'object' && result.error.data) {
          errorMessage = (result.error.data as any)?.message || errorMessage;
        } else if ('message' in result.error) {
          errorMessage = (result.error as any).message || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      if (result.data) {
        // Open the download URL in a new tab
        window.open(result.data, '_blank');
        show('Download started successfully', '', 'success');
      } else {
        throw new Error('No download URL received');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Download failed. Please try again.';
      setError(errorMessage);
      show(errorMessage, '', 'error');
      throw err;
    } finally {
      setDownloadingReports(prev => prev.filter(id => id !== reportId));
    }
  };

  return {
    downloadReport,
    isLoading: downloadingReports.length > 0,
    error,
    downloadingReports,
  };
};